/**
 * The page before `Publish v15` on a workspace (§5.25, Claude Design kit 73), in
 * place of the dialog that showed a document's raw paths and offered all or
 * nothing: what goes out, line by line and in the words of the rows, then what
 * never goes. One control per line, its tick. A line left unticked stays in the
 * draft (`publish-plan.ts`). The verb that commits stands in a foot that stays in
 * view, with what it does and its message (kit 74, Q3): the page bar only leads
 * back to editing.
 *
 * Every path and value came out of a document and is only ever drawn as text. A
 * secret's value is never drawn.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import type { ChangeGroup, ChangeLine } from '@airlock/contracts/workspace/change-groups';
import { Kicker } from '../../components/primitives';
import { Note, PageBody, PageLede } from '../../components/list';
import { color, font } from '../../theme/tokens';
import { HeaderButton } from '../../components/Shell';
import { DiffValue, isUnset, previewOf } from './DiffView';
import type { PublishPlan } from './publish-plan';
import { ProblemList } from './shared';

/** A commit subject, not a changelog: what the route refuses past. */
const MAX_MESSAGE = 500;

/** What each member keeps, by the names a member knows them by: the settings about one person's use of Airlock. */
const MEMBER_KEEPS = ['Session size', 'Default model', 'Log retention', 'Notifications'];

const plural = (count: number, one: string): string => `${count} ${one}${count === 1 ? '' : 's'}`;

/** What a publish does with what is ticked, said once under the page's verb. */
export function publishStatus(plan: PublishPlan): string {
  const going = plan.going.length;
  const waiting = plan.lines.length - going;
  if (!going) return 'Nothing is ticked, so nothing goes out.';
  return [
    `${plural(going, 'change')} ${going === 1 ? 'goes' : 'go'} out`,
    waiting ? `${waiting} ${waiting === 1 ? 'stays' : 'stay'} in your draft` : ''
  ]
    .filter(Boolean)
    .join(' · ');
}

export function PublishReview({
  version,
  plan,
  problems,
  busy,
  onToggle,
  onPublish
}: {
  /** The version on the page, whose number the next one follows. */
  version: number;
  plan: PublishPlan;
  /** What the Worker refused, in words. */
  problems: string[];
  busy: boolean;
  onToggle: (paths: string[], on: boolean) => void;
  /** Sends what is ticked, with the line written here if one was. */
  onPublish: (message: string) => void;
}): React.JSX.Element {
  const waiting = plan.lines.length - plan.going.length;
  const [message, setMessage] = useState('');
  const over = message.length > MAX_MESSAGE;
  const reason = !plan.lines.length
    ? 'Nothing to publish yet'
    : !plan.going.length
      ? 'Tick at least one change.'
      : plan.problems.length
        ? 'Fix what is listed first.'
        : over
          ? `Shorten the message to ${MAX_MESSAGE} characters.`
          : undefined;
  return (
    <>
    <PageBody>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: color.ink }}>Publish v{version + 1}</h2>
        <PageLede>
          Members read it at their next check. v{version} stays in History, and Revert brings it back.
        </PageLede>
        <ProblemList problems={[...plan.problems, ...problems]} />
        <section aria-label="Going out" style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Tick
              state={plan.going.length === 0 ? 'none' : waiting === 0 ? 'all' : 'some'}
              label="Send every change"
              disabled={busy}
              onChange={(on) => onToggle(plan.lines, on)}
            />
            <Kicker>Going out</Kicker>
            <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint }}>
              {plan.going.length} of {plan.lines.length}
            </span>
          </div>
          <ChangeRows groups={plan.groups} going={new Set(plan.going)} busy={busy} onToggle={onToggle} />
        </section>
        <section aria-label="Each member keeps their own" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Kicker>Each member keeps their own</Kicker>
            <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint }}>{MEMBER_KEEPS.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {MEMBER_KEEPS.map((name) => (
              <span
                key={name}
                style={{
                  padding: '8px 0',
                  fontSize: 13.5,
                  color: color.ink,
                  boxShadow: `inset 0 -1px 0 ${color.hairSoft}`
                }}
              >
                {name}
              </span>
            ))}
          </div>
          <Note>
            These and the other settings about one person’s use of Airlock are in no version. Only a lock in the
            organization’s settings imposes one.
            {plan.dropped > 0 && ` v${version} still holds ${plan.dropped} of them; this version drops them.`}
          </Note>
        </section>
      </div>
    </PageBody>
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        padding: '12px 24px',
        background: color.surface,
        boxShadow: `inset 0 1px 0 ${color.line}`
      }}
    >
      <label style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ display: 'flex', fontSize: 12.5, color: color.inkFaint }}>
          <span style={{ flex: 1 }}>Message — optional, shown in the history beside your name</span>
          <span style={{ fontFamily: font.mono, fontSize: 12, color: over ? color.badText : color.inkFaint }}>
            {message.length} / {MAX_MESSAGE}
          </span>
        </span>
        <input
          value={message}
          disabled={busy}
          aria-invalid={over || undefined}
          placeholder="What changed, in a line"
          onChange={(event) => setMessage(event.target.value)}
          style={{
            height: 32,
            padding: '0 10px',
            fontSize: 13.5,
            color: color.ink,
            background: color.surface,
            border: `1px solid ${over ? color.badText : color.line}`,
            borderRadius: 8
          }}
        />
      </label>
      <span role="status" style={{ fontSize: 12.5, color: color.inkFaint }}>
        {publishStatus(plan)}
      </span>
      <HeaderButton primary disabled={busy} reason={busy ? undefined : reason} onClick={() => onPublish(message.trim())}>
        {busy ? 'Publishing' : `Publish v${version + 1}`}
      </HeaderButton>
    </div>
    </>
  );
}

/** A diff in words: read-only, or a choice when it is told what is going. */
export function ChangeRows({
  groups,
  going,
  busy = false,
  onToggle
}: {
  groups: readonly ChangeGroup[];
  going?: ReadonlySet<string>;
  busy?: boolean;
  onToggle?: (paths: string[], on: boolean) => void;
}): React.JSX.Element {
  if (!groups.length) return <p style={{ margin: 0, fontSize: 13.5, color: color.inkFaint }}>Nothing changed.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
      {groups.map((group) => {
        const paths = group.lines.map((line) => line.path);
        const ticked = going ? paths.filter((path) => going.has(path)).length : paths.length;
        return (
          <div key={group.key} style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: 32,
                boxShadow: `inset 0 -1px 0 ${color.line}`
              }}
            >
              {going && onToggle && (
                <Tick
                  state={ticked === 0 ? 'none' : ticked === paths.length ? 'all' : 'some'}
                  label={`Send all of ${group.title}`}
                  disabled={busy}
                  onChange={(on) => onToggle(paths, on)}
                />
              )}
              <span style={{ fontSize: 13.5, fontWeight: 600, color: color.ink, overflowWrap: 'anywhere' }}>
                {group.title}
                {group.folder && (
                  <span style={{ marginLeft: 8, fontFamily: font.mono, fontSize: 12, fontWeight: 400, color: color.inkFaint }}>
                    {group.folder}
                  </span>
                )}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint }}>
                {going ? `${ticked} of ${paths.length}` : paths.length}
              </span>
            </div>
            {group.lines.map((line) => (
              <Row
                key={line.path}
                line={line}
                secret={group.key === 'secrets'}
                on={!going || going.has(line.path)}
                tick={
                  going && onToggle ? (
                    <Tick
                      state={going.has(line.path) ? 'all' : 'none'}
                      label={`Send ${line.label}`}
                      disabled={busy}
                      onChange={(on) => onToggle([line.path], on)}
                    />
                  ) : null
                }
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function Row({
  line,
  secret,
  on,
  tick
}: {
  line: ChangeLine;
  secret: boolean;
  on: boolean;
  tick: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 0',
        boxShadow: `inset 0 -1px 0 ${color.hairSoft}`
      }}
    >
      {tick}
      <div style={{ flex: 1, minWidth: 0, opacity: on ? 1 : 0.55 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, color: color.ink, overflowWrap: 'anywhere' }}>{line.label}</span>
          <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>{secret ? 'sealed' : line.kind}</span>
          {!on && <span style={{ fontSize: 12.5, color: color.inkFaint }}>stays in your draft</span>}
        </div>
        {!secret && !line.whole && line.kind !== 'added' && line.before !== undefined && (
          <DiffValue
            label="Before"
            value={valueText(line, line.before)}
            tone={isUnset(line.before) ? color.inkFaint : color.badText}
            struck={!isUnset(line.before)}
          />
        )}
        {!secret && !line.whole && line.kind !== 'removed' && line.after !== undefined && (
          <DiffValue label="After" value={valueText(line, line.after)} tone={color.okText} />
        )}
      </div>
    </div>
  );
}

/** A workspace setting left empty is not "null": each member's install answers it. */
function valueText(line: ChangeLine, value: unknown): string {
  return value === null && line.path.startsWith('config.') ? 'inherited' : previewOf(value);
}

/** A tick that can say *some*, which a checkbox's attribute alone cannot carry. */
function Tick({
  state,
  label,
  disabled,
  onChange
}: {
  state: 'all' | 'some' | 'none';
  label: string;
  disabled: boolean;
  onChange: (on: boolean) => void;
}): React.JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={state === 'all'}
      disabled={disabled}
      onChange={() => onChange(state !== 'all')}
      style={{ flex: 'none', width: 16, height: 16, margin: '2px 0 0', accentColor: color.ink }}
    />
  );
}
